import { useFormikContext } from 'formik';
import { Flex } from 'lib/components/styles/layout';
import {
  ParagraphNormal,
  ParagraphNormal14,
} from 'lib/components/styles/typography';
import {
  BRAND_STATUS,
  ERROR_CAMPAIGN_STATUSES,
} from 'lib/const/SuperAdminConstants';
import { SuperAdminCustomerDetails } from 'lib/context';
import React from 'react';
import { StatusBadge } from 'react-covideo-common';
import {
  FieldLabel,
  FormProps,
  Row,
  Section,
  SectionTitle,
} from '../../../components/AdminFormComponents';
import { FormikInputField } from 'lib/components/formik/FormikInputField';

export const Sms10DlcSection = (props: FormProps) => {
  const { isNewCustomer } = props;
  const { isSubmitting, values } =
    useFormikContext<SuperAdminCustomerDetails>();

  // Consider error if (a brand id is null and brand status is empty) or brand registration failed
  const isBrandError =
    !!values.customer10Dlc?.id &&
    ((!values.customer10Dlc?.brandStatus?.length &&
      !values.customer10Dlc.telnyxBrandId?.length) ||
      values.customer10Dlc?.brandStatus === BRAND_STATUS.REGISTRATION_FAILED);

  // Consider error if (a campaign id is not null and campaign status is empty) or campaign registration failed
  // If campaign id is null then campaign request is not submitted yet, so we don't want to show error in that case
  const isCampaignError =
    !!values.customer10Dlc?.id &&
    ((!values.customer10Dlc?.campaignStatus?.length &&
      !!values.customer10Dlc.telnyxCampaignId?.length) ||
      ERROR_CAMPAIGN_STATUSES.includes(
        values.customer10Dlc?.campaignStatus || ''
      ));
  // if SMS was just enabled, allow editing of 10DLC fields even if it's not a new customer, to allow them to fix any errors with brand or campaign that are preventing approval
  const smsIsJustEnabled =
    values.smsEnabled &&
    !values.customer10Dlc?.id &&
    !values.smsProfileId?.length;

  // Allow edit for new customer, or if there's an error with brand or campaign that needs to be fixed
  const isEditable = isNewCustomer || smsIsJustEnabled;

  return (
    <Section style={{ paddingTop: '0.5rem' }}>
      <SectionTitle>SMS 10DLC</SectionTitle>
      <Flex flexDirection='column' gap='2px'>
        <FormikInputField
          isRequired={true}
          name='customer10Dlc.legalCompanyName'
          label='Legal Business Name (W9)'
          type='text'
          readOnly={!isEditable || isSubmitting}
          labelPosition='left'
          value={values.customer10Dlc?.legalCompanyName || ''}
          extendStyles={{
            labelStyles: {
              width: '150px',
            },
            mainWrapper: {
              gap: '16px',
            },
          }}
        />
        <FormikInputField
          isRequired={true}
          name='customer10Dlc.legalStreet'
          label='Legal Street'
          type='text'
          readOnly={!isEditable || isSubmitting}
          labelPosition='left'
          value={values.customer10Dlc?.legalStreet || ''}
          extendStyles={{
            labelStyles: {
              width: '150px',
            },
            mainWrapper: {
              gap: '16px',
            },
          }}
        />
        <FormikInputField
          isRequired={true}
          name='customer10Dlc.legalCity'
          label='Legal City'
          type='text'
          readOnly={!isEditable || isSubmitting}
          labelPosition='left'
          value={values.customer10Dlc?.legalCity || ''}
          extendStyles={{
            labelStyles: {
              width: '150px',
            },
            mainWrapper: {
              gap: '16px',
            },
          }}
        />

        <FormikInputField
          isRequired={true}
          name='customer10Dlc.legalState'
          label='Legal State'
          type='text'
          readOnly={!isEditable || isSubmitting}
          labelPosition='left'
          value={values.customer10Dlc?.legalState || ''}
          extendStyles={{
            labelStyles: {
              width: '150px',
            },
            mainWrapper: {
              gap: '16px',
            },
          }}
        />

        <FormikInputField
          isRequired={true}
          name='customer10Dlc.legalPostalCode'
          label='Legal Postal Code'
          type='text'
          readOnly={!isEditable || isSubmitting}
          labelPosition='left'
          value={values.customer10Dlc?.legalPostalCode || ''}
          extendStyles={{
            labelStyles: {
              width: '150px',
            },
            mainWrapper: {
              gap: '16px',
            },
          }}
        />
        <FormikInputField
          isRequired={true}
          name='customer10Dlc.ein'
          label='EIN (W9)'
          type='text'
          readOnly={!isEditable || isSubmitting}
          labelPosition='left'
          value={values.customer10Dlc?.ein || ''}
          extendStyles={{
            labelStyles: {
              width: '150px',
            },
            mainWrapper: {
              gap: '16px',
            },
          }}
        />

        <FormikInputField
          isRequired={true}
          name='customer10Dlc.websiteUrl'
          label='Website URL'
          type='text'
          readOnly={!isEditable || isSubmitting}
          labelPosition='left'
          value={values.customer10Dlc?.websiteUrl || ''}
          extendStyles={{
            labelStyles: {
              width: '150px',
            },
            mainWrapper: {
              gap: '16px',
            },
          }}
        />
        <FormikInputField
          isRequired={true}
          name='customer10Dlc.w9ContactEmail'
          label='Dealer Contact Email'
          type='text'
          readOnly={!isEditable || isSubmitting}
          labelPosition='left'
          value={values.customer10Dlc?.w9ContactEmail || ''}
          extendStyles={{
            labelStyles: {
              width: '150px',
            },
            mainWrapper: {
              gap: '16px',
            },
          }}
        />
        <FormikInputField
          isRequired={true}
          name='customer10Dlc.w9ContactPhone'
          label='Dealer Contact Phone Number'
          type='text'
          readOnly={!isEditable || isSubmitting}
          labelPosition='left'
          value={values.customer10Dlc?.w9ContactPhone || ''}
          extendStyles={{
            labelStyles: {
              width: '150px',
            },
            mainWrapper: {
              gap: '16px',
            },
          }}
        />
        <FormikInputField
          isRequired={true}
          name='customer10Dlc.sampleVehicle'
          label='Sample Vehicle'
          type='text'
          readOnly={!isEditable || isSubmitting}
          labelPosition='left'
          value={values.customer10Dlc?.sampleVehicle || ''}
          extendStyles={{
            labelStyles: {
              width: '150px',
            },
            mainWrapper: {
              gap: '16px',
            },
          }}
        />
        {!isNewCustomer && (
          <>
            {!!values.customer10Dlc?.telnyxBrandId?.length && (
              <Row>
                <FieldLabel>Brand ID</FieldLabel>
                <Flex>
                  <ParagraphNormal>
                    {values.customer10Dlc.telnyxBrandId}
                  </ParagraphNormal>
                </Flex>
              </Row>
            )}
            {values.customer10Dlc?.telnyxCampaignId != null &&
              values.customer10Dlc?.telnyxCampaignId !== '' && (
                <Row>
                  <FieldLabel>Campaign ID</FieldLabel>
                  <Flex>
                    <ParagraphNormal>
                      {values.customer10Dlc.telnyxCampaignId}
                    </ParagraphNormal>
                  </Flex>
                </Row>
              )}
            <Row>
              <FieldLabel>Brand Status</FieldLabel>
              <Flex>
                <StatusBadge
                  text={values.customer10Dlc?.brandStatus || '-'}
                  variant={isBrandError ? 'error' : 'info'}
                />
                {isBrandError && (
                  <ParagraphNormal14>
                    To resubmit the 10DLC request, disable and enable the SMS
                    settings again.
                  </ParagraphNormal14>
                )}
              </Flex>
            </Row>
            <Row>
              <FieldLabel>Campaign Status</FieldLabel>
              <Flex>
                <StatusBadge
                  text={values.customer10Dlc?.campaignStatus || '-'}
                  variant={isCampaignError ? 'error' : 'info'}
                />
                {isCampaignError && (
                  <ParagraphNormal14>
                    To resubmit the 10DLC request, disable and enable the SMS
                    settings again.
                  </ParagraphNormal14>
                )}
              </Flex>
            </Row>
          </>
        )}
      </Flex>
    </Section>
  );
};
